import React from 'react';
import { CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';

const ComplianceMetrics = ({ location, timeframe }) => {
  const complianceData = [
    {
      category: 'HAZID Completion',
      required: 45,
      completed: 42,
      overdue: 3,
      percentage: 93.3,
      trend: 'up',
    },
    {
      category: 'Risk Assessment Reviews',
      required: 38,
      completed: 36,
      overdue: 2,
      percentage: 94.7,
      trend: 'up',
    },
    {
      category: 'Deviation Analysis',
      required: 52,
      completed: 47,
      overdue: 5,
      percentage: 90.4,
      trend: 'down',
    },
    {
      category: 'Safeguard Verification',
      required: 67,
      completed: 64,
      overdue: 3,
      percentage: 95.5,
      trend: 'up',
    },
    {
      category: 'Work Permit Linkage',
      required: 89,
      completed: 81,
      overdue: 8,
      percentage: 91.0,
      trend: 'up',
    },
  ];

  const overallCompliance = (complianceData?.reduce((sum, item) => sum + item?.percentage, 0) / complianceData?.length)?.toFixed(1);

  const totalOverdue = complianceData?.reduce((sum, item) => sum + item?.overdue, 0);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Compliance Metrics</h2>
        <p className="text-sm text-gray-600 mt-1">
          Risk management compliance tracking
        </p>
      </div>
      {/* Overall Compliance */}
      <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-medium text-purple-700 mb-1">Overall Compliance</div>
            <div className="text-4xl font-bold text-purple-900">{overallCompliance}%</div>
          </div>
          <div className="p-3 bg-purple-600 rounded-full">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
        </div>
        <div className="w-full bg-purple-200 rounded-full h-3">
          <div
            className="bg-purple-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${overallCompliance}%` }}
          ></div>
        </div>
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-purple-700">Target: 95%</span>
          <span className="flex items-center text-purple-700">
            <TrendingUp className="w-4 h-4 mr-1" />
            +2.3% vs last month
          </span>
        </div>
      </div>
      {/* Alert Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
          <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
          <div className="text-xl font-bold text-green-900">270</div>
          <div className="text-xs text-green-700">Compliant</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <XCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <div className="text-xl font-bold text-red-900">{totalOverdue}</div>
          <div className="text-xs text-red-700">Overdue</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
          <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
          <div className="text-xl font-bold text-yellow-900">15</div>
          <div className="text-xs text-yellow-700">Due Soon</div>
        </div>
      </div>
      {/* Compliance Details */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Compliance by Category</h3>
        <div className="space-y-4">
          {complianceData?.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{item?.category}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-900">{item?.percentage}%</span>
                  {item?.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  ) : (
                    <TrendingUp className="w-4 h-4 text-red-600 transform rotate-180" />
                  )}
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    item?.percentage >= 95 ? 'bg-green-500' :
                    item?.percentage >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${item?.percentage}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">
                  {item?.completed}/{item?.required} completed
                </span>
                {item?.overdue > 0 && (
                  <span className="text-red-600 font-medium">
                    {item?.overdue} overdue
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="pt-4 border-t border-gray-200">
        <button className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors">
          View Overdue Items
        </button>
      </div>
    </div>
  );
};

export default ComplianceMetrics;